/**
 * English strings for the open.kuber-coin.com frontend.
 *
 * Keys are dot-namespaced by surface (nav.*, common.*, hero.*, repos.*, footer.*).
 * Other locales mirror this shape; missing keys fall back to this file.
 */
export const en = {
  "nav.home": "Home",
  "nav.repos": "Repositories",
  "nav.issues": "Good First Issues",
  "nav.developers": "Developers",
  "nav.submit": "Submit Your Project",

  "common.loading": "Loading…",
  "common.error": "Something went wrong",
  "common.retry": "Try again",
  "common.search": "Search",

  "hero.title": "KuberCoin Open Source Platform",
  "hero.subtitle":
    "Discover, contribute to, and fund open-source projects in the KuberCoin ecosystem.",
  "hero.cta_browse": "Browse projects",
  "hero.cta_submit": "Submit your project",

  "repos.stars": "stars",
  "repos.forks": "forks",
  "repos.language": "Language",

  "footer.copyright": "© 2026 KuberCoin Open Source",
  "footer.license": "Released under the MIT license.",

  // --- Navigation (Wave 2) ---
  "nav.playground": "Playground",
  "nav.changelog": "Changelog",
  "nav.apiDocs": "API Docs",
  "nav.contributors": "Contributors",
  "nav.devLogin": "Dev login",
  "nav.admin": "Admin",
  "nav.logout": "Log out",

  // --- Search ---
  "search.title": "Search",
  "search.placeholder": "Search repos, files, submissions…",
  "search.button": "Search",
  "search.filter.all": "All",
  "search.filter.repos": "Repos",
  "search.filter.files": "Files",
  "search.filter.submissions": "Submissions",
  "search.type.repository": "Repository",
  "search.type.file": "File",
  "search.type.submission": "Submission",
  "search.empty": "No results found. Try a different search term.",

  // --- Notifications ---
  "notifications.title": "Notifications",
  "notifications.markAllRead": "Mark all read",
  "notifications.markRead": "Mark read",
  "notifications.empty": "No notifications yet.",

  // --- Contributors directory ---
  "contributors.title": "Contributors",
  "contributors.subtitle":
    "Meet the developers building KuberCoin's open-source ecosystem.",
  "contributors.searchPlaceholder": "Search contributors…",
  "contributors.sort.activity": "Most active",
  "contributors.sort.newest": "Newest",
  "contributors.sort.oldest": "Oldest",
  "contributors.sort.name": "Name A–Z",
  "contributors.emptySearch": "No contributors match your search.",
  "contributors.empty": "No contributors yet.",

  // --- Pagination (shared) ---
  "pagination.previous": "Previous",
  "pagination.next": "Next",

  // --- Rate limiting ---
  "rateLimit.slowDown": "Rate limit exceeded — please slow down",

  // --- 404 ---
  "notFound.title": "Page not found",
  "notFound.message":
    "The page you're looking for doesn't exist or has been moved.",
  "notFound.home": "Back to home",

  // --- Settings ---
  "settings.title": "Settings",
  "settings.theme": "Theme",
  "settings.themeDescription": "Choose your preferred color theme.",
  "settings.switchToLight": "Switch to light",
  "settings.switchToDark": "Switch to dark",
  "settings.language": "Language",
  "settings.languageDescription": "Choose your preferred language.",
  "settings.notifications": "Notifications",
  "settings.emailNotifications": "Email notifications",

  // --- Session ---
  "session.expired": "Your session has expired. Please sign in again.",

  // --- Offline ---
  "offline.banner": "You are offline. Some features may be unavailable.",

  // --- Shortcuts ---
  "shortcuts.title": "Keyboard Shortcuts",
  "shortcuts.hint": "Press ? to toggle this dialog",

  // --- Activity feed ---
  "activity.title": "Activity Feed",
  "activity.subtitle":
    "Recent activity across the KuberCoin open-source ecosystem.",
  "activity.empty": "No recent activity.",

  // --- Issues ---
  "issues.title": "Good First Issues",
  "issues.subtitle": "Find beginner-friendly issues to start contributing.",
  "issues.empty": "No open issues at the moment.",
  "issues.difficulty.all": "All",
  "issues.difficulty.beginner": "Beginner",
  "issues.difficulty.intermediate": "Intermediate",
  "issues.viewRepo": "View repo",

  // --- API Docs ---
  "apiDocs.title": "API Documentation",
  "apiDocs.subtitle": "Explore the KuberCoin Open API specification.",
  "apiDocs.loading": "Loading API specification…",
  "apiDocs.error": "Failed to load API specification.",

  // --- Time ago ---
  "time.justNow": "just now",
  "time.minutesAgo": "{n}m ago",
  "time.hoursAgo": "{n}h ago",
  "time.daysAgo": "{n}d ago",

  // --- Empty states ---
  "empty.generic": "Nothing here yet.",
  "empty.noResults": "No results found.",
} as const;

export type MessageKey = keyof typeof en;
